import { Button } from 'finara-components';
import { QRCodeSVG } from 'qrcode.react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import OtpInput from 'react-otp-input';

import type { MfaEnrollResponse } from '@/services/mfa.service';
import { mfaService } from '@/services/mfa.service';

interface MfaSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type SetupStep = 'intro' | 'qr-code' | 'verify';

export const MfaSetupModal: React.FC<MfaSetupModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState<SetupStep>('intro');
  const [enrollData, setEnrollData] = useState<MfaEnrollResponse | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);

  const handleStartEnrollment = async () => {
    setIsEnrolling(true);
    try {
      const data = await mfaService.enrollMFA();
      setEnrollData(data);
      setStep('qr-code');
    } catch (error) {
      toast.error('Error al iniciar la configuración de MFA');
      console.error('Error al registrar MFA:', error);
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!enrollData || otpCode.length !== 6) {
      toast.error('Por favor ingresa un código de 6 dígitos');
      return;
    }

    setIsVerifying(true);
    try {
      await mfaService.verifyEnrollment(enrollData.id, otpCode);
      toast.success('¡Autenticación de dos factores activada exitosamente!');
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error('Código incorrecto. Intenta nuevamente.');
      setOtpCode('');
      console.error('Error al verificar MFA:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    setStep('intro');
    setEnrollData(null);
    setOtpCode('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black opacity-60 cursor-default"
        onClick={handleClose}
        type="button"
        aria-label="Cerrar modal"
      />

      {/* Modal */}
      <div
        role="presentation"
        className="relative bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 max-w-lg w-full"
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Step 1: Introduction */}
          {step === 'intro' && (
            <>
              <h2 className="text-xl font-bold text-brand-white mb-4">Configurar Autenticación de Dos Factores</h2>

              <div className="space-y-4 mb-6">
                <p className="text-muted">
                  La autenticación de dos factores (2FA) agrega una capa adicional de seguridad a
                  tu cuenta.
                </p>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <h3 className="font-semibold text-brand-white mb-2">Necesitarás:</h3>
                  <ul className="list-disc list-inside text-sm text-muted space-y-1">
                    <li>Una aplicación de autenticación (Google Authenticator, Authy, etc.)</li>
                    <li>Tu teléfono móvil</li>
                    <li>Unos minutos para completar la configuración</li>
                  </ul>
                </div>

                <p className="text-sm text-muted">
                  Después de configurar 2FA, se te pedirá un código de verificación cada vez que
                  inicies sesión.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="black"
                  onClick={() => void handleStartEnrollment()}
                  disabled={isEnrolling}
                  className="flex-1"
                >
                  {isEnrolling ? 'Generando...' : 'Continuar'}
                </Button>
                <Button variant="white" onClick={handleClose} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Show QR Code */}
          {step === 'qr-code' && enrollData && (
            <>
              <h2 className="text-xl font-bold text-brand-white mb-4">Escanea el Código QR</h2>

              <div className="space-y-4 mb-6">
                <p className="text-muted">
                  Usa tu aplicación de autenticación para escanear este código QR:
                </p>

                {/* QR Code — white background required for readability */}
                <div className="flex justify-center p-6 bg-white border-2 border-border rounded-lg">
                  <QRCodeSVG value={enrollData.totp.uri} size={200} level="H" />
                </div>

                {/* Manual Entry */}
                <div className="bg-black/5 dark:bg-white/5 border border-border rounded-lg p-4">
                  <p className="text-sm font-semibold text-brand-white mb-2">
                    ¿No puedes escanear el código?
                  </p>
                  <p className="text-xs text-muted mb-2">
                    Ingresa este código manualmente en tu aplicación:
                  </p>
                  <div className="bg-surface border border-border rounded px-3 py-2">
                    <code className="text-sm font-mono text-brand-white break-all">
                      {enrollData.totp.secret}
                    </code>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="black" onClick={() => setStep('verify')} className="flex-1">
                  Siguiente
                </Button>
                <Button variant="white" onClick={handleClose} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Verify Code */}
          {step === 'verify' && enrollData && (
            <>
              <h2 className="text-xl font-bold text-brand-white mb-4">Verificar Código</h2>

              <div className="space-y-4 mb-6">
                <p className="text-muted">
                  Ingresa el código de 6 dígitos que aparece en tu aplicación de autenticación:
                </p>

                {/* OTP Input */}
                <div className="flex justify-center">
                  <OtpInput
                    value={otpCode}
                    onChange={setOtpCode}
                    numInputs={6}
                    renderSeparator={<span className="mx-2 text-muted">-</span>}
                    renderInput={(props: React.InputHTMLAttributes<HTMLInputElement>) => (
                      <input
                        {...props}
                        className="!w-12 h-14 text-center text-xl font-semibold border-2 border-border bg-surface text-brand-white rounded-lg focus:border-brand-blue focus:outline-none"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    )}
                    shouldAutoFocus
                  />
                </div>

                <p className="text-sm text-muted text-center">
                  El código cambia cada 30 segundos
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="black"
                  onClick={() => void handleVerifyCode()}
                  disabled={isVerifying || otpCode.length !== 6}
                  className="flex-1"
                >
                  {isVerifying ? 'Verificando...' : 'Verificar y Activar'}
                </Button>
                <Button
                  variant="white"
                  onClick={() => {
                    setStep('qr-code');
                    setOtpCode('');
                  }}
                  className="flex-1"
                >
                  Atrás
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
